import { supabase } from "../supabase";

export type CockpitStats = {
  total_users: number;
  nouveaux_semaine: number;
  actifs_30j: number;
  total_dossiers: number;
  dossiers_ci: number;
  dossiers_sn: number;
  dossiers_cm: number;
  nb_ecritures: number;
  nb_factures: number;
};

export type CockpitUserRow = {
  user_id: string;
  email: string;
  nom_complet: string | null;
  prenom: string | null;
  nom: string | null;
  pays: string | null;
  created_at: string | null;
  last_sign_in_at: string | null;
  nb_dossiers: number;
  statut: string;
};

export type CockpitUsersResponse = {
  rows: CockpitUserRow[];
  total: number;
};

export type CockpitDossierRow = {
  dossier_id: string;
  nom_dossier: string;
  owner_email: string;
  owner_user_id: string;
  pays: string | null;
  regime_fiscal: string | null;
  created_at: string | null;
  nb_ecritures: number;
};

export type CockpitDossiersResponse = {
  rows: CockpitDossierRow[];
  total: number;
};

export type CockpitUserDetail = {
  user: {
    user_id: string;
    email: string;
    created_at: string | null;
    last_sign_in_at: string | null;
  };
  profile: Record<string, unknown> | null;
  dossiers: {
    dossier_id: string;
    nom_dossier: string;
    pays: string | null;
    regime_fiscal: string | null;
    created_at: string | null;
    lien: string;
  }[];
  connexions: { type: string; at: string | null }[];
};

export type CockpitDossierDetail = {
  dossier: {
    id: string;
    nom: string;
    pays: string | null;
    regime_fiscal: string | null;
    type_comptabilite: string | null;
    created_at: string | null;
    owner_user_id: string;
    owner_email: string | null;
  };
  membres: {
    user_id: string;
    role: string;
    acces_gestion: boolean;
    acces_compta: boolean;
    email: string;
    nom_complet: string | null;
  }[];
  stats: {
    nb_ecritures: number;
    nb_factures_vente: number;
    nb_factures_achat: number;
  };
  infos: Record<string, unknown> | null;
};

export type CockpitActivity = {
  periode_jours: number;
  nouvelles_ecritures: number;
  nouvelles_factures_vente: number;
  nouvelles_factures_achat: number;
};

/** Vérifie la présence dans admin_users (RLS : une ligne seulement si superadmin). */
export async function checkCockpitAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase.from("admin_users").select("id").eq("user_id", userId).maybeSingle();
  if (error) return false;
  return !!data;
}

/**
 * Données alignées sur la vue SQL `admin_vue_stats` (requête côté client via RPC sécurisé).
 */
export async function fetchCockpitStats(): Promise<CockpitStats> {
  const { data, error } = await supabase.rpc("cockpit_stats");
  if (error) throw error;
  return data as CockpitStats;
}

export async function fetchCockpitUsers(params: {
  pays?: string;
  statut?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<CockpitUsersResponse> {
  const { data, error } = await supabase.rpc("cockpit_users", {
    p_pays: params.pays?.trim() || null,
    p_statut: params.statut?.trim() || null,
    p_search: params.search?.trim() || null,
    p_limit: params.limit ?? 50,
    p_offset: params.offset ?? 0,
  });
  if (error) throw error;
  return data as CockpitUsersResponse;
}

export async function fetchCockpitUserDetail(userId: string): Promise<CockpitUserDetail | null> {
  const { data, error } = await supabase.rpc("cockpit_user_detail", { p_user_id: userId });
  if (error) throw error;
  return data as CockpitUserDetail | null;
}

export async function fetchCockpitDossiers(params: {
  pays?: string;
  regime?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<CockpitDossiersResponse> {
  const { data, error } = await supabase.rpc("cockpit_dossiers", {
    p_pays: params.pays?.trim() || null,
    p_regime: params.regime?.trim() || null,
    p_search: params.search?.trim() || null,
    p_limit: params.limit ?? 50,
    p_offset: params.offset ?? 0,
  });
  if (error) throw error;
  return data as CockpitDossiersResponse;
}

export async function fetchCockpitDossierDetail(dossierId: string): Promise<CockpitDossierDetail | null> {
  const { data, error } = await supabase.rpc("cockpit_dossier_detail", { p_dossier_id: dossierId });
  if (error) throw error;
  return data as CockpitDossierDetail | null;
}

export async function fetchCockpitActivity(days = 7): Promise<CockpitActivity> {
  const { data, error } = await supabase.rpc("cockpit_activity", { p_days: days });
  if (error) throw error;
  return data as CockpitActivity;
}
